import { useMemo, useCallback } from 'react';
import { useAppStore } from '../store';
import { getStageTargets } from '../services/thresholds';
import { deriveClimateMetrics } from '../services/psychrometrics';
import { STAGE_LABELS } from '../types';
import type { CultivationStage } from '../types';
import { supabase } from '../services/supabase';

export function useAppMetrics() {
  const climateSettings = useAppStore(s => s.climateSettings);
  const suggestedStage  = useAppStore(s => s.suggestedStage);
  const current         = useAppStore(s => s.current);
  const readings        = useAppStore(s => s.readings);
  const primarySpeciesId = useAppStore(s => s.primarySpeciesId);
  const setClimateSettings = useAppStore(s => s.setClimateSettings);
  const addSettingsLog  = useAppStore(s => s.addSettingsLog);
  const tents           = useAppStore(s => s.tents);

  const activeStage: CultivationStage = climateSettings.autoStageDetect ? suggestedStage : climateSettings.stage;

  const derivedCurrent = useMemo(() =>
    current ? deriveClimateMetrics(current.temperature, current.humidity) : null
  , [current]);

  const sensorConfidence = useMemo(() =>
    readings.length > 0 ? Math.min(100, Math.round((readings.length / 24) * 100)) : null
  , [readings]);

  const sensorReadingCount = readings.length;

  const loadStagePreset = useCallback((stage: CultivationStage) => {
    const t = getStageTargets(primarySpeciesId, stage);
    const prevStage = climateSettings.stage;

    setClimateSettings((prev: typeof climateSettings) => ({
      ...prev,
      stage,
      temperature:     { min: t.temperature.min, max: t.temperature.max, ideal: t.temperature.ideal },
      humidity:        { min: t.humidity.min,    max: t.humidity.max,    ideal: t.humidity.ideal },
      co2:             { min: t.co2.min,         max: t.co2.max,         ideal: t.co2.ideal },
      vpd:             { min: t.vpd!.min,        max: t.vpd!.max,        ideal: t.vpd!.ideal },
      dewPoint:        { min: t.dewPoint!.min,   max: t.dewPoint!.max,   ideal: t.dewPoint!.ideal },
      humidityDeficit: { min: t.humidityDeficit!.min, max: t.humidityDeficit!.max, ideal: t.humidityDeficit!.ideal },
    }));

    const now = new Date().toISOString();

    if (prevStage !== stage) {
      addSettingsLog({
        timestamp: now,
        category: 'climate',
        description: `Evre değiştirildi: ${STAGE_LABELS[prevStage]} → ${STAGE_LABELS[stage]} (preset yüklendi)`
      });
    }

    if (supabase) {
      const tentIds = tents.length > 0 ? tents.map(t => t.id) : ['tent-1', 'tent-2'];
      const rules = tentIds.flatMap(tentId => [
        {
          id: `${tentId}-temp`,
          tent_id: tentId,
          metric: 'temperature',
          min_value: t.temperature.min,
          max_value: t.temperature.max,
          warning_margin: 1,
          enabled: true,
          quiet_minutes: 30,
          escalate_after_minutes: 30,
          updated_at: now,
        },
        {
          id: `${tentId}-humidity`,
          tent_id: tentId,
          metric: 'humidity',
          min_value: t.humidity.min,
          max_value: t.humidity.max,
          warning_margin: 3,
          enabled: true,
          quiet_minutes: 30,
          escalate_after_minutes: 30,
          updated_at: now,
        },
        {
          id: `${tentId}-co2`,
          tent_id: tentId,
          metric: 'co2',
          min_value: null,
          max_value: t.co2.max,
          warning_margin: 150,
          enabled: true,
          quiet_minutes: 30,
          escalate_after_minutes: 30,
          updated_at: now,
        },
      ]);

      const client = supabase;
      if (client) {
        client.from('alert_rules').upsert(rules, { onConflict: 'id' })
          .then(() => {
            return client.from('alert_rule_state')
              .update({ last_alerted_at: null })
              .in('rule_id', rules.map(r => r.id));
          })
          .then(() => {
            // SRE Alignment: Sync the growth phase and mapped species back to public.tents
            const dbSpecies = primarySpeciesId.replace('-', '_');
            const finalDbSpecies = dbSpecies === 'lion_mane' ? 'lions_mane' : dbSpecies;
            const activeTentId = climateSettings.tentId || 'tent-1';
            
            return client.from('tents')
              .update({
                current_phase: stage,
                current_species: finalDbSpecies
              })
              .eq('id', activeTentId);
          })
          .then(undefined, () => {
            // Silent catch
          });
      }
    }
  }, [primarySpeciesId, setClimateSettings, climateSettings.stage, climateSettings.tentId, addSettingsLog, tents]);

  const setActiveStageManual = useCallback((stage: CultivationStage) => {
    loadStagePreset(stage);
  }, [loadStagePreset]);

  const toggleAutoStage = useCallback(() => {
    setClimateSettings((prev: typeof climateSettings) => ({ ...prev, autoStageDetect: !prev.autoStageDetect }));
  }, [setClimateSettings]);

  return {
    activeStage,
    derivedCurrent,
    sensorConfidence,
    sensorReadingCount,
    loadStagePreset,
    setActiveStageManual,
    toggleAutoStage
  };
}
